import { useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  ReferenceLine
} from 'recharts';
import { calculateCompoundInterest, generateChartData } from '../../utils/calculations';
import { getTaxConfig, INVESTMENT_TYPES } from '../../utils/taxEngine';
import { formatCurrency, formatCompactNumber } from '../../utils/formatters';
import { BarChart3 } from 'lucide-react';

const CustomTooltip = ({ active, payload, label, t, language }) => {
  if (active && payload && payload.length) {
    const isPaused = payload[0]?.payload?.isPaused;
    return (
      <div className="bg-popover/95 backdrop-blur-sm border border-border rounded-xl p-4 shadow-xl">
        <p className="font-semibold mb-2 text-sm flex items-center gap-2">
          {t('year')} {label}
          {isPaused && (
            <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded">
              {language === 'ro' ? 'Pauză' : 'Paused'}
            </span>
          )}
        </p>
        <div className="space-y-1.5 text-sm">
          {payload.map((entry, index) => (
            <div key={index} className="flex justify-between gap-4">
              <span className="text-muted-foreground flex items-center gap-2">
                <span 
                  className="w-2.5 h-2.5 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                />
                {entry.name}
              </span>
              <span className="font-semibold">{formatCurrency(entry.value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

export const InvestmentChart = () => {
  const { inputs, t, theme, language } = useApp();
  
  // Calculate chart data
  const chartData = useMemo(() => {
    const taxConfig = getTaxConfig(inputs.investmentType);
    const isUnitLinked = inputs.investmentType === INVESTMENT_TYPES.UNIT_LINKED;
    
    const calcParams = {
      initialDeposit: inputs.initialDeposit,
      monthlyContribution: inputs.monthlyContribution,
      annualInterestRate: inputs.annualInterestRate,
      years: inputs.years,
      compoundingFrequency: inputs.compoundingFrequency,
      contributionFrequency: inputs.contributionFrequency,
      taxRate: inputs.includeTaxes ? taxConfig.interestTaxRate : 0,
      taxOnEachPeriod: taxConfig.taxOnEachPeriod,
      adminFeeRate: isUnitLinked ? inputs.adminFeeRate : 0,
      pauseStartYear: inputs.pauseEnabled ? inputs.pauseStartYear : 0,
      pauseEndYear: inputs.pauseEnabled ? inputs.pauseEndYear : 0,
      useSimpleInterest: taxConfig.useSimpleInterest,
    };
    
    const result = calculateCompoundInterest(calcParams);
    return generateChartData(result.yearlyBreakdown);
  }, [inputs]);
  
  const isDark = theme === 'dark';
  
  // Find pause years for reference lines
  const pauseStart = inputs.pauseEnabled ? inputs.pauseStartYear : null;
  const pauseEnd = inputs.pauseEnabled ? inputs.pauseEndYear : null;
  
  return (
    <Card 
      className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden"
      data-testid="investment-chart"
    >
      <CardHeader className="pb-4 border-b border-border/50">
        <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10">
            <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          {t('chartTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        <div className="h-[300px] sm:h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorContributions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorInterest" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={isDark ? '#27272a' : '#e2e8f0'} 
                vertical={false}
              />
              <XAxis 
                dataKey="year" 
                stroke={isDark ? '#71717a' : '#94a3b8'}
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke={isDark ? '#71717a' : '#94a3b8'}
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => formatCompactNumber(value)}
                width={60}
              />
              <Tooltip 
                content={<CustomTooltip t={t} language={language} />}
                cursor={{ stroke: isDark ? '#3f3f46' : '#cbd5e1', strokeWidth: 1 }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) => (
                  <span className="text-sm text-muted-foreground">{value}</span>
                )}
              />
              
              {/* Reference lines for pause period */}
              {pauseStart && (
                <ReferenceLine 
                  x={pauseStart} 
                  stroke="#f97316" 
                  strokeDasharray="5 5"
                  label={{ 
                    value: language === 'ro' ? 'Start pauză' : 'Pause start', 
                    position: 'top',
                    fill: '#f97316',
                    fontSize: 10
                  }}
                />
              )}
              {pauseEnd && (
                <ReferenceLine 
                  x={pauseEnd} 
                  stroke="#f97316" 
                  strokeDasharray="5 5"
                  label={{ 
                    value: language === 'ro' ? 'Final pauză' : 'Pause end', 
                    position: 'top',
                    fill: '#f97316',
                    fontSize: 10
                  }}
                />
              )}
              
              <Area
                type="monotone"
                dataKey="contributions"
                name={t('contributions')}
                stackId="1"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#colorContributions)"
                animationDuration={1000}
                animationEasing="ease-out"
              />
              <Area
                type="monotone"
                dataKey="interest"
                name={t('interest')}
                stackId="1"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#colorInterest)"
                animationDuration={1000}
                animationEasing="ease-out"
                animationBegin={200}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default InvestmentChart;
